import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { useAuth } from '../context/AuthContext';

// Escucha los deep links de la app nativa. Cuando el backend redirige a
// maldolor://auth/success?token=... tras el login con Google, cierra el navegador
// del sistema, canjea el token y entra. Solo actua en nativo; en web no hace nada.
//
// loginWithToken y navigate son estables (useCallback / router), asi que el efecto
// se registra una sola vez. El ref evita canjes dobles si llegan dos eventos.
export const AuthDeepLinkListener: React.FC = () => {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const processing = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // addListener es asincrono: la bandera cancelled evita que el listener quede
    // registrado si el componente se desmonta antes de que la promesa resuelva.
    let listenerHandle: { remove: () => void } | undefined;
    let cancelled = false;

    CapApp.addListener('appUrlOpen', async ({ url }) => {
      if (!url.startsWith(NATIVE_PREFIX)) return;
      if (processing.current) return;
      processing.current = true;

      try {
        await Browser.close().catch(() => {});
        const token = new URL(url).searchParams.get('token');
        if (token) {
          await loginWithToken(token);
          navigate('/');
        } else {
          navigate('/login?error=oauth_failed');
        }
      } catch {
        navigate('/login?error=oauth_error');
      } finally {
        processing.current = false;
      }
    }).then((handle) => {
      if (cancelled) handle.remove();
      else listenerHandle = handle;
    });

    return () => {
      cancelled = true;
      listenerHandle?.remove();
    };
  }, [navigate, loginWithToken]);

  return null;
};

const NATIVE_PREFIX = 'maldolor://auth/success';
